export const CSV_DELIMITERS = [",", ";", "\t", "|"] as const;

export type CsvDelimiter = (typeof CSV_DELIMITERS)[number];

export const detectDelimiter = (text: string): CsvDelimiter => {
  const lines = text
    .split(/\r?\n/)
    .filter((line) => line.trim().length > 0)
    .slice(0, 5);
  const counts = new Map<CsvDelimiter, number>();
  CSV_DELIMITERS.forEach((delimiter) => counts.set(delimiter, 0));
  for (const line of lines) {
    CSV_DELIMITERS.forEach((delimiter) => {
      const current = counts.get(delimiter) ?? 0;
      counts.set(delimiter, current + (line.split(delimiter).length - 1));
    });
  }
  let best: CsvDelimiter = ";";
  let bestCount = -1;
  for (const [delimiter, count] of counts.entries()) {
    if (count > bestCount) {
      best = delimiter;
      bestCount = count;
    }
  }
  return best;
};

export const parseCsvRows = (text: string, delimiter: CsvDelimiter) => {
  const rows: string[][] = [];
  let row: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    if (char === '"') {
      const next = text[index + 1];
      if (inQuotes && next === '"') {
        current += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (char === delimiter && !inQuotes) {
      row.push(current.trim());
      current = "";
      continue;
    }
    if ((char === "\n" || char === "\r") && !inQuotes) {
      if (char === "\r" && text[index + 1] === "\n") {
        index += 1;
      }
      row.push(current.trim());
      if (row.some((cell) => cell.length > 0)) {
        rows.push(row);
      }
      row = [];
      current = "";
      continue;
    }
    current += char;
  }
  if (current.length > 0 || row.length > 0) {
    row.push(current.trim());
    if (row.some((cell) => cell.length > 0)) {
      rows.push(row);
    }
  }
  return rows;
};

export const normalizeHeader = (value: string) =>
  value
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/[^a-z0-9]/g, "");

export const DATE_PATTERNS = [
  { format: "dd.MM.yyyy", regex: /^\d{2}\.\d{2}\.\d{4}$/ },
  { format: "dd-MM-yyyy", regex: /^\d{2}-\d{2}-\d{4}$/ },
  { format: "dd/MM/yyyy", regex: /^\d{2}\/\d{2}\/\d{4}$/ },
  { format: "yyyy-MM-dd", regex: /^\d{4}-\d{2}-\d{2}$/ },
] as const;

export type DateFormat = (typeof DATE_PATTERNS)[number]["format"];

export const guessDateFormat = (values: string[]) => {
  for (const pattern of DATE_PATTERNS) {
    if (values.some((value) => pattern.regex.test(value))) {
      return pattern.format;
    }
  }
  return null;
};

const isDateLike = (value: string) => DATE_PATTERNS.some((pattern) => pattern.regex.test(value.trim()));

export const parseDateValue = (value: string, format: DateFormat | null) => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  if (!format) {
    return null;
  }
  if (format === "yyyy-MM-dd") {
    return trimmed;
  }
  const separator = format.includes(".") ? "." : format.includes("/") ? "/" : "-";
  const parts = trimmed.split(separator);
  if (parts.length !== 3) {
    return null;
  }
  const [partA, partB, partC] = parts;
  const day = format.startsWith("dd") ? partA : partC;
  const month = format.startsWith("dd") ? partB : partB;
  const year = format.startsWith("dd") ? partC : partA;
  if (!day || !month || !year) {
    return null;
  }
  return `${year.padStart(4, "0")}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
};

export type AmountFormat = "comma" | "dot" | null;

export const guessAmountFormat = (values: string[]) => {
  let commaScore = 0;
  let dotScore = 0;
  values.forEach((raw) => {
    const value = raw.trim();
    if (!value) {
      return;
    }
    if (/,\d{1,2}$/.test(value)) {
      commaScore += 1;
    }
    if (/\.\d{1,2}$/.test(value)) {
      dotScore += 1;
    }
  });
  if (commaScore === 0 && dotScore === 0) {
    return null;
  }
  return commaScore >= dotScore ? "comma" : "dot";
};

export const parseAmountValue = (value: string, format: AmountFormat) => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  let normalized = trimmed.replace(/\s/g, "").replace(/[^0-9,.-]/g, "");
  if (!normalized) {
    return null;
  }
  if (format === "comma") {
    normalized = normalized.replace(/\./g, "").replace(",", ".");
  } else if (format === "dot") {
    normalized = normalized.replace(/,/g, "");
  } else {
    const hasComma = normalized.includes(",");
    const hasDot = normalized.includes(".");
    if (hasComma && hasDot) {
      normalized = normalized.replace(/\./g, "").replace(",", ".");
    } else if (hasComma) {
      normalized = normalized.replace(",", ".");
    }
  }
  const parsed = Number.parseFloat(normalized);
  return Number.isNaN(parsed) ? null : parsed;
};

export const guessHasHeader = (rows: string[][]) => {
  const firstRow = rows[0];
  if (!firstRow) {
    return false;
  }
  let textCount = 0;
  let numericCount = 0;
  for (const cell of firstRow) {
    if (/[a-zA-ZæøåÆØÅ]/.test(cell)) {
      textCount += 1;
    }
    if (/\d/.test(cell)) {
      numericCount += 1;
    }
  }
  return textCount > numericCount;
};

export const guessColumnIndex = (
  headers: string[] | null,
  rows: string[][],
  headerKeywords: string[],
  valueMatcher: (value: string) => boolean,
) => {
  if (headers) {
    const matchIndex = headers.findIndex((header) => {
      const normalized = normalizeHeader(header);
      return headerKeywords.some((keyword) => normalized.includes(keyword));
    });
    if (matchIndex >= 0) {
      return matchIndex;
    }
  }
  let bestIndex = -1;
  let bestScore = -1;
  const sampleRows = rows.slice(0, 10);
  const maxColumns = sampleRows.reduce((max, row) => Math.max(max, row.length), 0);
  for (let columnIndex = 0; columnIndex < maxColumns; columnIndex += 1) {
    let score = 0;
    for (const row of sampleRows) {
      if (valueMatcher(row[columnIndex] ?? "")) {
        score += 1;
      }
    }
    if (score > bestScore) {
      bestScore = score;
      bestIndex = columnIndex;
    }
  }
  return bestIndex >= 0 ? bestIndex : null;
};

export const guessFieldMapping = (headers: string[] | null, rows: string[][]) => {
  const dateIndex = guessColumnIndex(
    headers,
    rows,
    ["date", "dato"],
    (value) => DATE_PATTERNS.some((pattern) => pattern.regex.test(value.trim())),
  );
  const amountIndex = guessColumnIndex(
    headers,
    rows,
    ["amount", "belob", "beloeb", "sum", "amount"],
    (value) => !isDateLike(value) && parseAmountValue(value, null) !== null,
  );
  const descriptionIndex = guessColumnIndex(
    headers,
    rows,
    ["description", "tekst", "text", "beskrivelse", "reference"],
    (value) => /[a-zA-ZæøåÆØÅ]/.test(value),
  );
  const normalizedDescription =
    descriptionIndex === dateIndex || descriptionIndex === amountIndex ? null : descriptionIndex;
  const fallbackDescription =
    normalizedDescription ??
    (() => {
      const maxColumns = rows.reduce((max, row) => Math.max(max, row.length), 0);
      for (let index = 0; index < maxColumns; index += 1) {
        if (index !== dateIndex && index !== amountIndex) {
          return index;
        }
      }
      return null;
    })();
  return {
    date: dateIndex,
    description: fallbackDescription,
    amount: amountIndex,
  };
};
